/**
 * Introduction tooltip for the Share chat button in the Evidence chat header.
 *
 * Shown once per device to authenticated users who have established enough
 * engagement with Evidence (> MIN_CHATS_TO_SHOW_INTRO saved chats) the first
 * time they land on a saved chat where the share button is rendered.
 * Dismissed via close, outside-click or the CTA, all of which mark it as
 * seen in localStorage so it does not reappear.
 *
 * Gate order (all must pass):
 *   1. Parent-provided `enabled` (chat route + auth + share feature flag)
 *   2. `useIsShareEvidenceChatIntroEnabled` feature flag
 *   3. User has > MIN_CHATS_TO_SHOW_INTRO chats created
 *   4. User has not already seen / dismissed the tooltip on this device
 */

import { hasSeenShareChatCoachmarkAtom } from '@/atoms/chatAtoms';
import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightBeacon,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { useIsShareEvidenceChatIntroEnabled } from '@/features/flags';
import { useQueryChats } from '@/hooks/query/useQueryChats';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useAtom } from 'jotai';
import { Share } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { track } from '@/lib/analytics/track';
import { trackButtonClicked } from '@/lib/analytics/scribe/button';

/** Delay before auto-open so we don't flash during route transitions. */
const AUTO_OPEN_DELAY_MS = 800;

/**
 * Minimum number of saved chats a user must have before the intro is shown.
 * Targets established Evidence users who are most likely to benefit from
 * sharing their answers with colleagues.
 */
const MIN_CHATS_TO_SHOW_INTRO = 3;

interface ShareChatOnboardingTooltipProps {
  /** The trigger element, typically the ShareChatButton. */
  children: ReactNode;
  /**
   * Whether the tooltip is eligible to show. Parent composes this from any
   * route / auth / feature checks it owns (e.g. "is the share button
   * currently rendered for this user"). When false, the component renders
   * its children untouched.
   */
  enabled: boolean;
  /**
   * Called when the user clicks the in-tooltip CTA. Should open the share
   * modal. The tooltip marks itself seen automatically before invoking.
   */
  onShareClick: () => void;
}

export const ShareChatOnboardingTooltip = ({
  children,
  enabled,
  onShareClick,
}: ShareChatOnboardingTooltipProps) => {
  const [hasSeen, setHasSeen] = useAtom(hasSeenShareChatCoachmarkAtom);
  const [isOpen, setIsOpen] = useState(false);
  const hasDismissedRef = useRef(false);
  const hasScheduledOpenRef = useRef(false);
  const hasTrackedViewRef = useRef(false);

  const isFlagEnabled = useIsShareEvidenceChatIntroEnabled();
  const { data: chatData, isLoading: isChatCountLoading } = useQueryChats(1, 1);
  const chatCount = chatData?.total ?? 0;
  const hasEnoughChats = chatCount > MIN_CHATS_TO_SHOW_INTRO;

  // All gates must pass before we even schedule the open. We intentionally
  // delay until the chat count has loaded — otherwise the tooltip would
  // briefly open before the count response filters it out.
  const shouldShow =
    enabled && isFlagEnabled && !hasSeen && !isChatCountLoading && hasEnoughChats;

  useEffect(() => {
    if (!shouldShow) return;
    if (hasDismissedRef.current) return;
    if (hasScheduledOpenRef.current) return;

    hasScheduledOpenRef.current = true;
    const timer = window.setTimeout(() => {
      if (!hasDismissedRef.current) {
        setIsOpen(true);
      }
    }, AUTO_OPEN_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [shouldShow]);

  useEffect(() => {
    if (!isOpen) return;
    if (hasTrackedViewRef.current) return;
    hasTrackedViewRef.current = true;
    track({ options: { amplitude: false },
      event_name: TrackingEventName.ModalViewed,
      props: { screen_name: ScreenName.ShareChatOnboardingTooltip },
    });
  }, [isOpen]);

  const markSeen = useCallback(() => {
    hasDismissedRef.current = true;
    setHasSeen(true);
    setIsOpen(false);
  }, [setHasSeen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setIsOpen(true);
        return;
      }
      trackButtonClicked({
        screenName: ScreenName.ShareChatOnboardingTooltip,
        action: ButtonAction.ShareChatOnboardingDismissedClose,
        label: 'Dismiss share chat intro',
      });
      markSeen();
    },
    [markSeen]
  );

  const handleShareClick = useCallback(() => {
    trackButtonClicked({
      screenName: ScreenName.ShareChatOnboardingTooltip,
      action: ButtonAction.ShareChatOnboardingShareClick,
      label: 'Share',
    });
    markSeen();
    // Give the spotlight a beat to unmount before opening the share modal so
    // the dialog focus-trap doesn't fight the closing animation.
    window.setTimeout(() => {
      onShareClick();
    }, 120);
  }, [markSeen, onShareClick]);

  if (!shouldShow) {
    return <>{children}</>;
  }

  return (
    <Spotlight open={isOpen} onOpenChange={handleOpenChange}>
      <SpotlightAnchor asChild>
        <SpotlightBeacon active={isOpen}>{children}</SpotlightBeacon>
      </SpotlightAnchor>
      <SpotlightContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="z-50"
        data-testid="share-chat-onboarding-spotlight"
      >
        <SpotlightArrow />
        <SpotlightHeader icon={<Share aria-hidden />}>
          <SpotlightTitle>
            <FormattedMessage
              defaultMessage="Share this chat"
              id="share.chat.onboarding.title"
            />
          </SpotlightTitle>
          <SpotlightClose data-testid="share-chat-onboarding-close" />
        </SpotlightHeader>
        <SpotlightDescription>
          <FormattedMessage
            defaultMessage="Share this evidence chat with anyone via a link — no sign-in needed."
            id="share.chat.onboarding.description"
          />
        </SpotlightDescription>
        <SpotlightFooter className="justify-end">
          <Button
            type="button"
            size="xs"
            variant="secondary"
            onClick={handleShareClick}
            className="bg-surface text-text-primary hover:bg-background-secondary"
            data-testid="share-chat-onboarding-cta"
          >
            <FormattedMessage
              defaultMessage="Share"
              id="share.chat.onboarding.cta"
            />
          </Button>
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
};
